import {
  Align,
  Box,
  Heading,
  Hide,
  Show,
  Space,
  Spinner,
  Text,
  useElementSize,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled, {useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {currencyCodes, getSafely, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../i18n/i18n';
import {CarTooltip} from '../../pages/StatisticsPage/components/Chart/components/CarTooltip';
import {MY_VEHICLE_COLOR} from '../../pages/StatisticsPage/components/Chart/consts/myVehicleColor';
import {ExtendedStatisticsCar} from '../../pages/StatisticsPage/components/Chart/types/ExtendedStatisticsCar';
import {formatChartPriceLabel} from '../../pages/StatisticsPage/components/Chart/utils/formatChartPriceLabel';
import {PriceReportType} from '../../types/PriceReportType';
import {isSoldIn30Days} from '../../utils/isSoldIn30Days';

interface PriceDistributionChartProps extends TestIdProps {
  priceReport: PriceReportType;
  /** Vehicles to distribute — one unit rectangle each (advertised and/or sold). */
  cars?: ExtendedStatisticsCar[];
  /**
   * Plain advertised prices to distribute — used on the dashboard, where the chart
   * mirrors the baseline price chart's frozen dataset (prices only, so no hover).
   * Ignored when {@link cars} is provided.
   */
  prices?: number[];
  /** The vehicle's own recommended price (CZK); drawn as the "Tento vůz" marker. */
  myPrice?: number;
  isLoading?: boolean;
  /** Grow the chart to fill its container's height (statistics page) instead of a fixed height. */
  fillHeight?: boolean;
  /** Statistics-page charts render without their own title and description. */
  isTitleHidden?: boolean;
  /**
   * 'thisVehicle' (default, dashboard): blue marker labelled “Tento vůz”.
   * 'recommendedPrice' (statistics): near-black marker labelled with the price itself.
   */
  markerVariant?: 'thisVehicle' | 'recommendedPrice';
}

/** Fixed height on the dashboard; a fallback until the container is measured when filling. */
const CHART_HEIGHT = 322;
/** Minimum chart height as a spacing-scale unit (≈ CHART_HEIGHT). */
const CHART_MIN_HEIGHT_UNITS = 80;
const PADDING_TOP = 56;
const PADDING_BOTTOM = 56;
const SLOT_COUNT = 50;
const QUINTILE_COUNT = 5;
/** Below this container width the SVG scales down uniformly via the viewBox. */
const MIN_CHART_WIDTH = 560;
/** Unit squares for small samples; thin lines once a slot holds hundreds of cars. */
const MAX_UNIT_HEIGHT = 13;
const MIN_UNIT_HEIGHT = 0.6;
const CZK_PER_THOUSAND = 1000;
const TENTHS = 10;
const PERCENT = 100;
const AXIS_LABEL_EDGE_INSET = 16;
const MARKER_PILL_WIDTH = 66;
const MARKER_PILL_HEIGHT = 24;
/** Approximate glyph width of the pill font — sizes the pill to its label. */
const MARKER_PILL_CHAR_WIDTH = 7;
const MARKER_PILL_PADDING_X = 16;
/** Text baseline offset from the pill's vertical centre. */
const MARKER_PILL_TEXT_BASELINE = 5;
/** The pill sits just inside the tinted band zone so it never covers the % labels. */
const MARKER_PILL_TOP_INSET = 6;
const MARKER_BEAK_HALF = 5;
const MARKER_BEAK_DEPTH = 6;
/** Marker closer to the pill edge than this switches the beak to the corner shape. */
const MARKER_BEAK_INSET = 10;
/** How far the corner beak reaches up the pill edge to cover its rounded corner. */
const MARKER_BEAK_CORNER_COVER = 4;
const LEGEND_WIDTH = 200;
/** Gap between the hovered unit and the car tooltip above it. */
const TOOLTIP_MARGIN = 6;

/** Gap between stacked units shrinks as the unit height shrinks (spec F1). */
const UNIT_GAP_STEPS = [
  [6, 2.5],
  [3, 1],
  [1.7, 0.45],
] as const;
const MIN_UNIT_GAP = 0.12;
const UNIT_RADIUS_STEPS = [
  [4, 2],
  [2, 1],
] as const;

type CarCategory = 'advertised' | 'sold30' | 'sold90';

const getUnitGap = (unitHeight: number): number =>
  UNIT_GAP_STEPS.find(([threshold]) => unitHeight > threshold)?.[1] ?? MIN_UNIT_GAP;

const getUnitRadius = (unitHeight: number): number =>
  UNIT_RADIUS_STEPS.find(([threshold]) => unitHeight > threshold)?.[1] ?? 0;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

interface DistributionItem {
  price: number;
  /** Null for price-only datasets (dashboard) — such units are not hoverable. */
  car: ExtendedStatisticsCar | null;
}

interface HoveredUnit {
  car: ExtendedStatisticsCar;
  anchorX: number;
  anchorY: number;
}

/**
 * F1 price distribution — every comparable vehicle is one unit rectangle, binned by
 * price into 50 slots and stacked bottom-up; units thin into lines for large samples.
 * Advertised cars carry the orange quintile ramp, sold cars a neutral shade (matching
 * the statistics legend). The price axis is split into fifths labelled with the share
 * of cars, the recommended price sits on the "Tento vůz" marker, and hovering a unit
 * reveals that car in the shared statistics tooltip.
 */
export function PriceDistributionChart(props: PriceDistributionChartProps) {
  const theme = useTheme();
  const formatNumber = useFormatNumber();
  const [ref, containerWidth, containerHeight] = useElementSize<HTMLDivElement>();
  const [tooltipRef, tooltipWidth, tooltipHeight] = useElementSize<HTMLDivElement>();
  const [hovered, setHovered] = useState<HoveredUnit | null>(null);

  // One item per vehicle: full cars (statistics — hoverable) or bare prices
  // (dashboard — the baseline chart's frozen dataset carries no ad ids).
  const carItems: DistributionItem[] = (props.cars ?? [])
    .filter((car) => isNotNil(car.price_with_vat))
    .map((car) => ({price: car.price_with_vat ?? 0, car}));
  const priceItems: DistributionItem[] = (props.prices ?? []).map((price) => ({price, car: null}));
  const sortedItems = (isNotNil(props.cars) ? carItems : priceItems).sort(
    (left, right) => left.price - right.price
  );
  const low = sortedItems[0]?.price ?? 0;
  const high = sortedItems[sortedItems.length - 1]?.price ?? 0;
  const span = high - low;
  const total = sortedItems.length;

  // With the percentile bands tinted below, every advertised car reads in one orange.
  const advertisedColor = theme.colors.palettes.orange[60][100];
  // Light traffic-light backgrounds of the five price-field bands — cheap end green,
  // expensive end red — so it is obvious at a glance which band a car falls into.
  // Only the palest palette shades (10/20), matching the price gauge's pastel look.
  const bandColors = [
    theme.colors.palettes.green[10][100],
    theme.colors.palettes.green[20][100],
    theme.colors.palettes.yellow[20][100],
    theme.colors.palettes.orange[20][100],
    theme.colors.palettes.red[20][100],
  ];
  // Same colours the statistics chart/legend use: sold ≤30 days is the dark neutral
  // Flag, sold 31–90 days the light one — bumped one shade darker so it stays
  // readable on the tinted band backgrounds.
  const soldRecentColor = theme.colors.palettes.neutral[300][100];
  const soldOldColor = theme.colors.palettes.neutral[50][100];
  // The vehicle's own advertisement — same mint the main statistics chart uses.
  const myAdColor = theme.colors.palettes.green[50][100];
  const markerVariant = props.markerVariant ?? 'thisVehicle';
  const markerColor = match(markerVariant)
    .with('thisVehicle', () => theme.colors.palettes.blue[60][100])
    .with('recommendedPrice', () => MY_VEHICLE_COLOR)
    .exhaustive();
  // The price label matches the other charts' reference-line format (e.g. 295 tis. Kč).
  const recommendedCurrency = props.priceReport.sellingPrice.recommended.currency;
  const currencyCode = currencyCodes.find((code) => code === recommendedCurrency) ?? 'CZK';
  const markerLabel = match(markerVariant)
    .with('thisVehicle', () => i18n.t('entity.overviewStatisticsMileageChart.labels.thisVehicle'))
    .with('recommendedPrice', () =>
      formatChartPriceLabel(props.myPrice ?? 0, currencyCode, {formatNumber})
    )
    .exhaustive();
  // The pill grows with its label so longer price strings never overflow it.
  const markerPillWidth = Math.max(
    MARKER_PILL_WIDTH,
    Math.round(markerLabel.length * MARKER_PILL_CHAR_WIDTH) + MARKER_PILL_PADDING_X
  );
  // Axis/Δ labels in thousands: CZK rounds to whole thousands, other currencies
  // (euro amounts are ~25× smaller) keep one decimal place.
  const formatThousands = (price: number): string =>
    match(currencyCode)
      .with('CZK', () => formatNumber(Math.round(price / CZK_PER_THOUSAND)) ?? '')
      .otherwise(
        () => formatNumber(Math.round((price / CZK_PER_THOUSAND) * TENTHS) / TENTHS, 1) ?? ''
      );
  const paperColor = theme.colors.palettes.white[10][100];
  const separatorColor = theme.colors.palettes.neutral[40][100];
  const baselineColor = theme.colors.palettes.neutral[30][100];
  const textPrimary = theme.colors.text.primary;
  const textSecondary = theme.colors.text.secondary;
  const textTertiary = theme.colors.text.tertiary;

  // Match the sibling charts' typography instead of arbitrary sizes.
  const fontSizeEmphasis = theme.fontSizes.text.xSmall;
  const fontSizeSmall = theme.fontSizes.text.xxSmall;
  // Same size the reference-line pills use in the other statistics charts.
  const fontSizeMarker = theme.fontSizes.text.small;
  const weightRegular = theme.fontWeights.text.default;
  const weightAlternative = theme.fontWeights.text.alternative;

  const width = Math.max(MIN_CHART_WIDTH, containerWidth ?? MIN_CHART_WIDTH);
  // The SVG scales uniformly to the container, so SVG units map to screen pixels by this.
  const scale = isNotNil(containerWidth) ? containerWidth / width : 1;
  const chartHeight = props.fillHeight ? (containerHeight ?? CHART_HEIGHT) : CHART_HEIGHT;
  const plotHeight = chartHeight - PADDING_TOP - PADDING_BOTTOM;
  const baselineY = chartHeight - PADDING_BOTTOM;
  const slotWidth = width / SLOT_COUNT;

  const fractionOf = (price: number): number => (span > 0 ? (price - low) / span : 0);
  const slotOf = (price: number): number =>
    Math.min(SLOT_COUNT - 1, Math.floor(fractionOf(price) * SLOT_COUNT));
  const quintileOf = (price: number): number =>
    Math.min(QUINTILE_COUNT - 1, Math.floor(fractionOf(price) * QUINTILE_COUNT));
  const priceAtFraction = (fraction: number): number => low + span * fraction;

  const categoryOf = (item: DistributionItem): CarCategory => {
    if (isNotNil(item.car) && item.car.is_sold) {
      return isSoldIn30Days(props.priceReport, item.car) ? 'sold30' : 'sold90';
    }
    // Price-only datasets carry advertised prices exclusively.
    return 'advertised';
  };
  const categoryRank = (item: DistributionItem): number =>
    match(categoryOf(item))
      .with('advertised', () => 0)
      .with('sold30', () => 1)
      .with('sold90', () => 2)
      .exhaustive();
  const colorOf = (item: DistributionItem): string => {
    // The vehicle's own listing is highlighted regardless of its category.
    if (item.car?.isMyAd) {
      return myAdColor;
    }
    return match(categoryOf(item))
      .with('advertised', () => advertisedColor)
      .with('sold30', () => soldRecentColor)
      .with('sold90', () => soldOldColor)
      .exhaustive();
  };

  // Advertised cars stack at the bottom, sold cars on top, so a slot reads as its
  // in-advertisement base with the sold share above it.
  const slots = Array.from({length: SLOT_COUNT}, (_, slot) =>
    sortedItems
      .filter((item) => slotOf(item.price) === slot)
      .sort((left, right) => categoryRank(left) - categoryRank(right))
  );
  const maxSlotCount = Math.max(1, ...slots.map((slotCars) => slotCars.length));
  const unitHeight = Math.min(MAX_UNIT_HEIGHT, plotHeight / maxSlotCount);
  const unitGap = getUnitGap(unitHeight);
  const drawnUnitHeight = Math.max(MIN_UNIT_HEIGHT, unitHeight - unitGap);
  const unitRadius = getUnitRadius(unitHeight);

  const quintiles = Array.from({length: QUINTILE_COUNT}, (_, fifth) => {
    const boundaryX = (width * fifth) / QUINTILE_COUNT;
    const carsInFifth = sortedItems.filter((item) => quintileOf(item.price) === fifth).length;
    return {
      boundaryX,
      labelX: boundaryX + width / (QUINTILE_COUNT * 2),
      hasSeparator: fifth > 0,
      sharePercent: Math.round((carsInFifth / total) * PERCENT),
      // The od–do boundaries already sit on the bottom axis — the top shows the
      // price span one fifth covers instead of repeating them.
      rangeLabel: `Δ ${formatThousands(span / QUINTILE_COUNT)} k`,
    };
  });

  const units = slots.flatMap((slotItems, slotIndex) => {
    const x = slotIndex * slotWidth + 1;
    const centerX = slotIndex * slotWidth + slotWidth / 2;
    return slotItems.map((item, stackIndex) => {
      const y = baselineY - (stackIndex + 1) * unitHeight + unitGap / 2;
      return {
        key: item.car?.ad_id ?? `${x.toFixed(1)}-${y.toFixed(1)}`,
        x,
        y,
        centerX,
        color: colorOf(item),
        car: item.car,
      };
    });
  });

  const axisTicks = Array.from({length: QUINTILE_COUNT + 1}, (_, boundary) => ({
    tickX: clamp(
      (width * boundary) / QUINTILE_COUNT,
      AXIS_LABEL_EDGE_INSET,
      width - AXIS_LABEL_EDGE_INSET
    ),
    priceLabel: `${formatThousands(priceAtFraction(boundary / QUINTILE_COUNT))} k`,
    percentLabel: boundary * (PERCENT / QUINTILE_COUNT),
  }));

  // The line sits at the price's true position, clamped only to the chart edges: a
  // price below the cheapest car pins it to the far left, above the priciest far right.
  const markerX = isNotNil(props.myPrice)
    ? clamp(width * fractionOf(props.myPrice), 0, width)
    : null;
  const markerPillX = isNotNil(markerX)
    ? clamp(markerX - markerPillWidth / 2, 2, width - markerPillWidth - 2)
    : 0;
  const markerPillY = PADDING_TOP - 24 + MARKER_PILL_TOP_INSET;
  // Centred marker gets the usual triangle under the pill. At the chart edges the
  // pill stops centring over the line, and the beak becomes a speech-bubble corner
  // tail fused with the pill's left/right edge (the ActualPriceChart look).
  const markerPillBottom = markerPillY + MARKER_PILL_HEIGHT;
  const markerPillRight = markerPillX + markerPillWidth;
  const markerBeakPath = match(markerX ?? 0)
    .when(
      (x) => x <= markerPillX + MARKER_BEAK_INSET,
      () =>
        `M ${markerPillX} ${markerPillBottom - MARKER_BEAK_CORNER_COVER} L ${markerPillX + 2 * MARKER_BEAK_HALF} ${markerPillBottom} L ${markerPillX} ${markerPillBottom + MARKER_BEAK_DEPTH} Z`
    )
    .when(
      (x) => x >= markerPillRight - MARKER_BEAK_INSET,
      () =>
        `M ${markerPillRight} ${markerPillBottom - MARKER_BEAK_CORNER_COVER} L ${markerPillRight - 2 * MARKER_BEAK_HALF} ${markerPillBottom} L ${markerPillRight} ${markerPillBottom + MARKER_BEAK_DEPTH} Z`
    )
    .otherwise(
      (x) =>
        `M ${x - MARKER_BEAK_HALF} ${markerPillBottom} L ${x + MARKER_BEAK_HALF} ${markerPillBottom} L ${x} ${markerPillBottom + MARKER_BEAK_DEPTH} Z`
    );

  // Screen-pixel position of the car tooltip, anchored above the hovered unit.
  // The measured size keeps it clamped inside the chart, whose card clips overflow.
  // The overlay stays permanently mounted (hidden when nothing is hovered) so the
  // size observer is attached from the start and the measurement is always live.
  const containerScreenWidth = containerWidth ?? width;
  const tooltipHalfWidth = (tooltipWidth ?? 0) / 2;
  const tooltipLeft = clamp(
    (hovered?.anchorX ?? 0) * scale,
    tooltipHalfWidth,
    Math.max(tooltipHalfWidth, containerScreenWidth - tooltipHalfWidth)
  );
  const tooltipTop = Math.max((hovered?.anchorY ?? 0) * scale - TOOLTIP_MARGIN, tooltipHeight ?? 0);

  return (
    <Hide when={!props.isLoading && (total < 2 || span <= 0)}>
      <VStack height={props.fillHeight ? '100%' : undefined}>
        {/* Statistics-page charts carry no title of their own — dashboard cards do */}
        <Show when={!props.isTitleHidden}>
          <Align center>
            <Heading size={4}>
              {i18n.t('entity.overviewChart.labels.priceDistributionTitle')}
            </Heading>
          </Align>
          <Space vertical={2} />
          <Align center>
            <Text size="xSmall" color="secondary" align="center">
              {i18n.t('entity.overviewChart.labels.priceDistributionDescription')}
            </Text>
          </Align>
          <Space vertical={4} />
        </Show>
        <Box
          ref={ref}
          position="relative"
          flexGrow={props.fillHeight ? 1 : undefined}
          minHeight={CHART_MIN_HEIGHT_UNITS}
          data-testid={suffixTestId('wrapper', props)}
        >
          {props.isLoading ? (
            <Box height="100%" minHeight={CHART_MIN_HEIGHT_UNITS}>
              <Align center>
                <Space vertical={20} />
                <Spinner data-testid={suffixTestId('loading', props)} />
              </Align>
            </Box>
          ) : (
            <svg
              width="100%"
              height={props.fillHeight ? '100%' : undefined}
              viewBox={`0 0 ${width} ${chartHeight}`}
              role="img"
              onMouseLeave={() => setHovered(null)}
            >
              {/* Quintile band backgrounds + separators + share-of-cars labels */}
              {quintiles.map((quintile, fifth) => (
                <g key={quintile.boundaryX}>
                  <rect
                    x={quintile.boundaryX}
                    y={PADDING_TOP - 24}
                    width={width / QUINTILE_COUNT}
                    height={baselineY - (PADDING_TOP - 24)}
                    fill={getSafely(bandColors, fifth) ?? paperColor}
                  />
                  {quintile.hasSeparator ? (
                    <line
                      x1={quintile.boundaryX}
                      y1={PADDING_TOP - 24}
                      x2={quintile.boundaryX}
                      y2={baselineY}
                      stroke={separatorColor}
                      strokeDasharray="4 3"
                    />
                  ) : null}
                  <text
                    x={quintile.labelX}
                    y={PADDING_TOP - 26}
                    fontSize={fontSizeEmphasis}
                    fontWeight={weightAlternative}
                    fill={textPrimary}
                    textAnchor="middle"
                  >
                    {`${quintile.sharePercent} %`}
                  </text>
                  <text
                    x={quintile.labelX}
                    y={PADDING_TOP - 12}
                    fontSize={fontSizeSmall}
                    fontWeight={weightRegular}
                    fill={textTertiary}
                    textAnchor="middle"
                  >
                    {quintile.rangeLabel}
                  </text>
                </g>
              ))}

              {/* One rectangle per car, stacked bottom-up in its price slot.
                  Hover reveals the car tooltip only when real cars back the units. */}
              {units.map((unit) => {
                const hoveredCar = unit.car;
                return (
                  <rect
                    key={unit.key}
                    x={unit.x}
                    y={unit.y}
                    width={slotWidth - 2}
                    height={drawnUnitHeight}
                    rx={unitRadius}
                    fill={unit.color}
                    cursor={isNotNil(hoveredCar) ? 'pointer' : undefined}
                    onMouseEnter={
                      isNotNil(hoveredCar)
                        ? () =>
                            setHovered({car: hoveredCar, anchorX: unit.centerX, anchorY: unit.y})
                        : undefined
                    }
                    onMouseLeave={isNotNil(hoveredCar) ? () => setHovered(null) : undefined}
                  />
                );
              })}

              {/* Baseline + price/percent axis at each fifth boundary */}
              <line x1={0} y1={baselineY} x2={width} y2={baselineY} stroke={baselineColor} />
              {axisTicks.map((tick) => (
                <g key={tick.percentLabel}>
                  <text
                    x={tick.tickX}
                    y={baselineY + 16}
                    fontSize={fontSizeSmall}
                    fontWeight={weightRegular}
                    fill={textTertiary}
                    textAnchor="middle"
                  >
                    {tick.priceLabel}
                  </text>
                  <text
                    x={tick.tickX}
                    y={baselineY + 29}
                    fontSize={fontSizeSmall}
                    fontWeight={weightAlternative}
                    fill={textSecondary}
                    textAnchor="middle"
                  >
                    {`${tick.percentLabel} %`}
                  </text>
                </g>
              ))}

              {/* "Tento vůz" marker: vertical line, baseline dot, pill and beak */}
              {isNotNil(markerX) ? (
                <g>
                  <line
                    x1={markerX}
                    y1={markerPillY}
                    x2={markerX}
                    y2={baselineY}
                    stroke={markerColor}
                    strokeWidth={2}
                  />
                  <circle
                    cx={markerX}
                    cy={baselineY}
                    r={5}
                    fill={markerColor}
                    stroke={paperColor}
                    strokeWidth={2}
                  />
                  <rect
                    x={markerPillX}
                    y={markerPillY}
                    width={markerPillWidth}
                    height={MARKER_PILL_HEIGHT}
                    rx={5}
                    fill={markerColor}
                  />
                  <path d={markerBeakPath} fill={markerColor} />
                  <text
                    x={markerPillX + markerPillWidth / 2}
                    y={markerPillY + MARKER_PILL_HEIGHT / 2 + MARKER_PILL_TEXT_BASELINE}
                    fontSize={fontSizeMarker}
                    fontWeight={weightRegular}
                    fill={paperColor}
                    textAnchor="middle"
                  >
                    {markerLabel}
                  </text>
                </g>
              ) : null}

              {/* Legend bottom-right */}
              <rect
                x={width - LEGEND_WIDTH}
                y={chartHeight - 12}
                width={9}
                height={7}
                rx={2}
                fill={advertisedColor}
              />
              <text
                x={width - LEGEND_WIDTH + 13}
                y={chartHeight - 5}
                fontSize={fontSizeSmall}
                fontWeight={weightRegular}
                fill={textTertiary}
              >
                {i18n.t('entity.overviewChart.labels.priceDistributionLegend', {
                  total: formatNumber(total),
                })}
              </text>
            </svg>
          )}

          {/* Car popover — the same tooltip as the statistics chart, rendered as an HTML
              overlay (not inside the SVG) so it is never clipped by the chart bounds.
              Pixel-accurate positioning above the hovered unit; no platform primitive
              positions in raw pixels. */}
          <TooltipOverlay
            ref={tooltipRef}
            $left={tooltipLeft}
            $top={tooltipTop}
            $isVisible={isNotNil(hovered)}
          >
            {isNotNil(hovered) ? (
              <CarTooltip
                priceReport={props.priceReport}
                car={hovered.car}
                showShadow
                data-testid={suffixTestId('carTooltip', props)}
              />
            ) : null}
          </TooltipOverlay>
        </Box>
      </VStack>
    </Hide>
  );
}

interface TooltipOverlayProps {
  $left: number;
  $top: number;
  $isVisible: boolean;
}

/**
 * Pixel-anchored overlay for the car tooltip. The frequently changing position is
 * injected via the style attribute (the styled-components pattern for per-render
 * values), so no inline style prop appears in the JSX.
 */
const TooltipOverlay = styled.div.attrs<TooltipOverlayProps>(({$left, $top, $isVisible}) => ({
  style: {
    left: $left,
    top: $top,
    visibility: $isVisible ? ('visible' as const) : ('hidden' as const),
  },
}))<TooltipOverlayProps>`
  position: absolute;
  /* Natural content width so mileage and price in the tooltip never clip */
  width: max-content;
  transform: translate(-50%, -100%);
  pointer-events: none;
  z-index: 1;
`;
